import { useParams, Navigate } from "react-router-dom";
import SEO from "@/components/SEO";
import PageHero from "@/components/sections/PageHero";
import CTASection from "@/components/sections/CTASection";
import { Reveal } from "@/components/motion/Reveal";
import { Btn, Eyebrow, SectionHeading } from "@/components/primitives";
import PlanCard from "@/components/sections/PlanCard";
import { getIndustry } from "@/content/industries";
import { plans, brand } from "@/content/site";
import { AlertCircle, Wrench, Users, Compass, Check } from "lucide-react";

export default function IndustryDetail() {
  const { slug } = useParams();
  const industry = getIndustry(slug);
  if (!industry) return <Navigate to="/404" replace />;

  const recommendedPlan = plans.find((p) => p.id === industry.planGuidance);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: `Digital growth for ${industry.name}`,
    provider: { "@type": "Organization", name: brand.name, url: brand.siteUrl },
    areaServed: "AU",
    description: industry.heroCopy,
  };

  return (
    <>
      <SEO
        title={`${industry.name} Marketing & Growth Systems | Green Circle Group`}
        description={industry.heroCopy}
        path={`/industries/${industry.slug}`}
        image={industry.image}
        jsonLd={jsonLd}
      />
      <PageHero
        eyebrow={`Industry — ${industry.name}`}
        heading={industry.heroHeading}
        copy={industry.heroCopy}
        breadcrumbs={[
          { label: "Home", to: "/" },
          { label: "Industries", to: "/industries" },
          { label: industry.name, to: `/industries/${industry.slug}` },
        ]}
      >
        <Btn to="/contact">Book a Free Growth Assessment</Btn>
      </PageHero>

      {/* Image band */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="aspect-[21/9] rounded-[2rem] overflow-hidden border border-border-neutral">
            <img
              src={industry.image}
              alt={industry.alt}
              className="h-full w-full object-cover"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* Challenges */}
      <section className="bg-soft py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <Eyebrow>Common growth challenges</Eyebrow>
              <SectionHeading className="mt-5">
                What gets in the way for {industry.name.toLowerCase()}
              </SectionHeading>
              <div className="mt-8 space-y-4">
                {industry.challenges.map((c, i) => (
                  <Reveal
                    key={i}
                    delay={i * 0.05}
                    className="flex items-start gap-4 rounded-2xl bg-white border border-border-neutral p-5"
                  >
                    <AlertCircle className="h-5 w-5 text-mint shrink-0 mt-0.5" />
                    <p className="text-dark-neutral">{c}</p>
                  </Reveal>
                ))}
              </div>
            </div>
            <div className="space-y-6">
              <Reveal className="rounded-[1.75rem] bg-white border border-border-neutral p-8">
                <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-forest text-lime">
                  <Wrench className="h-6 w-6" />
                </div>
                <h3 className="mt-5 text-xl font-extrabold text-forest">
                  Services worth prioritising
                </h3>
                <ul className="mt-4 space-y-2.5">
                  {industry.jobTypes.map((j) => (
                    <li key={j} className="flex items-start gap-3 text-mid-neutral">
                      <Check className="h-4 w-4 text-mint mt-1 shrink-0" strokeWidth={3} />
                      {j}
                    </li>
                  ))}
                </ul>
              </Reveal>
              <Reveal delay={0.08} className="rounded-[1.75rem] bg-white border border-border-neutral p-8">
                <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-forest text-lime">
                  <Compass className="h-6 w-6" />
                </div>
                <h3 className="mt-5 text-xl font-extrabold text-forest">
                  How customers find and choose you
                </h3>
                <p className="mt-3 text-mid-neutral leading-relaxed">
                  {industry.howCustomersChoose}
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Recommended system */}
      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-5xl px-5 sm:px-8">
          <Reveal className="rounded-[2rem] bg-forest text-white p-8 md:p-12">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-lime text-forest">
              <Users className="h-6 w-6" />
            </div>
            <h2 className="mt-6 text-2xl md:text-3xl font-extrabold">
              The recommended Green Circle Group growth system
            </h2>
            <p className="mt-4 text-white/80 leading-relaxed text-lg">
              {industry.recommendedSystem}
            </p>
          </Reveal>

          {recommendedPlan && (
            <div className="mt-10">
              <Eyebrow>Suggested starting point</Eyebrow>
              <p className="mt-3 text-mid-neutral">
                Most {industry.name.toLowerCase()} start with{" "}
                <span className="font-bold text-forest">
                  {recommendedPlan.name}
                </span>
                . The Growth Assessment confirms the right fit for your market
                and capacity.
              </p>
              <div className="mt-6 max-w-md">
                <PlanCard plan={recommendedPlan} showAll={false} />
              </div>
            </div>
          )}
        </div>
      </section>

      <CTASection
        eyebrow={`Grow your ${industry.name.toLowerCase().replace(/s$/, "")} business`}
        heading={`Ready for more of the right customers to find your ${industry.name.toLowerCase()} business?`}
      />
    </>
  );
}
